import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { Agent } from "../../services";
import { useTypedSelector } from "../../redux/store";
import {
	isTextMatchingLeadingStarsFilterHelper,
	sortByNumberOrString,
} from "../../utils";
import { ColumnId } from "../../pages/MainPage/pages/Customers/tabs/Agents/components/TableFirst/columns";

import { isQuery, isLimit, isOrder } from ".";

const useAgentsSubHandler = (): useAgentsSubHandler.Props => {
	const { t } = useTranslation();
	const lang = useTypedSelector((state) => state.session.language);

	const getQueryAndLimitAgentsSubHandler = useCallback(
		(
			data: useAgentsSubHandler.SmallSubscribeOptions,
			roleSubscribe: Agent.Model[],
		): Agent.Model[] => {
			const { limit, query, order } = data;

			if (!Array.isArray(roleSubscribe)) {
				return [];
			}

			let retval = [...(roleSubscribe || [])];

			if (isQuery(query)) {
				retval = retval.filter(({ name }) => {
					const nameLang = name?.[lang];

					const isFindName =
						typeof nameLang === "string" &&
						isTextMatchingLeadingStarsFilterHelper(nameLang, query);
					if (isFindName) return true;

					return false;
				});
			}

			if (isOrder(order)) {
				const [[dataKey, sortType] = []] = Object.entries(order || {});
				const isSort =
					typeof sortType === "string" &&
					typeof dataKey === "string" &&
					["asc", "desc"].includes(sortType.toLowerCase());

				if (isSort) {
					const isAscending = sortType.toLowerCase() === "asc";
					const sortOrder = isAscending ? 1 : -1;

					if (dataKey === ColumnId.Title) {
						const sort = retval.sort((prev, next) => {
							const prevValue = prev.name?.[lang] ?? -1;
							const nextValue = next.name?.[lang] ?? -1;
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.TaxiService) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								prev.defaultTaxiService?.settlement?.[lang] ??
								-1;
							const nextValue =
								next.defaultTaxiService?.settlement?.[lang] ??
								-1;
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.Status) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								t(`statuses.${prev.status?.level}`) ?? -1;
							const nextValue =
								t(`statuses.${next.status?.level}`) ?? -1;
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.MobileApp) {
						const sort = retval.sort((prev, next) => {
							const prevValue = prev.isAccessToApp ? 1 : -1;
							const nextValue = next.isAccessToApp ? 1 : -1;
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.Group) {
						const sort = retval.sort((prev, next) => {
							const prevValue = prev.agentGroup?.name ?? -1;
							const nextValue = next.agentGroup?.name ?? -1;
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.ContactPersonPhone) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								prev.additionalFields?.contactPersonPhone ?? -1;
							const nextValue =
								next.additionalFields?.contactPersonPhone ?? -1;
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.ContactPersonName) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								prev.additionalFields?.contactPersonName ?? -1;
							const nextValue =
								next.additionalFields?.contactPersonName ?? -1;
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.Company) {
						const sort = retval.sort((prev, next) => {
							const prevValue = prev.company?.name?.[lang] ?? -1;
							const nextValue = next.company?.name?.[lang] ?? -1;
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.Cabinet) {
						const sort = retval.sort((prev, next) => {
							const prevValue = prev.isAccessToAccount ? 1 : -1;
							const nextValue = next.isAccessToAccount ? 1 : -1;
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.BonusBalance) {
						const sort = retval.sort((prev, next) => {
							const findMoney = (model: Agent.Model) =>
								model.paymentAccounts?.find(
									(a) => a.type === "bonus",
								)?.amount ?? 0;

							const prevValue = findMoney(prev);
							const nextValue = findMoney(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.Balance) {
						const sort = retval.sort((prev, next) => {
							const findMoney = (model: Agent.Model) =>
								model.paymentAccounts?.find(
									(a) => a.type === "main",
								)?.amount ?? 0;

							const prevValue = findMoney(prev);
							const nextValue = findMoney(next);
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}
				}
			}

			if (isLimit(limit)) {
				retval = retval.slice(0, limit);
			}

			return retval;
		},
		[lang, t],
	);

	return {
		getQueryAndLimitAgentsSubHandler,
	};
};

declare namespace useAgentsSubHandler {
	interface Props {
		getQueryAndLimitAgentsSubHandler: (
			data: SmallSubscribeOptions,
			roleSubscribe: Agent.Model[],
		) => Agent.Model[];
	}

	interface SmallSubscribeOptions {
		order?: Agent.Order;
		query?: string;
		limit?: number;
	}
}

export default useAgentsSubHandler;
